import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Phone, MapPin, CreditCard, Calendar, Shield, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function MonCompte() {
  const { user, updateUserInfo } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nom: user?.nom || '',
    email: user?.email || user?.username || '',
    telephone: user?.telephone || '',
    pays: user?.pays || '',
    password: ''
  });

  const handleSave = () => {
    const updates = {
      nom: formData.nom,
      email: formData.email,
      telephone: formData.telephone,
      pays: formData.pays
    };
    
    // Only update password if provided
    if (formData.password.trim()) {
      updates.password = formData.password;
    }

    const success = updateUserInfo(user.id, updates);
    if (success) {
      toast.success('Profil mis à jour avec succès');
      setIsEditing(false);
      setFormData({ ...formData, password: '' });
    } else {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const isPackActive = () => {
    if (!user?.fin_abonnement) return false;
    return new Date(user.fin_abonnement) > new Date();
  };

  const daysRemaining = () => {
    if (!user?.fin_abonnement) return 0;
    const end = new Date(user.fin_abonnement);
    const now = new Date();
    const diff = end - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8 animate-in">
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight">Mon Compte</h1>
          <p className="text-base text-muted-foreground">Gérez vos informations personnelles et votre abonnement</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <User className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">Informations personnelles</h2>
                </div>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    data-testid="edit-profile-button"
                    className="px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-sm font-medium"
                  >
                    Modifier
                  </button>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSave}
                      data-testid="save-profile-button"
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Enregistrer</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Nom complet</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      data-testid="edit-nom-input"
                      className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      placeholder="Votre nom complet"
                    />
                  ) : (
                    <p className="h-12 px-4 flex items-center rounded-lg bg-secondary/30 text-sm">
                      {user?.nom || 'Non renseigné'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>Email</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="edit-email-input"
                      className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      placeholder="votre@email.com"
                    />
                  ) : (
                    <p className="h-12 px-4 flex items-center rounded-lg bg-secondary/30 text-sm">
                      {user?.email || user?.username}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>Téléphone</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      data-testid="edit-telephone-input"
                      className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      placeholder="+1 000 000 0000"
                    />
                  ) : (
                    <p className="h-12 px-4 flex items-center rounded-lg bg-secondary/30 text-sm">
                      {user?.telephone || 'Non renseigné'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>Pays</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.pays}
                      onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                      data-testid="edit-pays-input"
                      className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      placeholder="Canada"
                    />
                  ) : (
                    <p className="h-12 px-4 flex items-center rounded-lg bg-secondary/30 text-sm">
                      {user?.pays || 'Non renseigné'}
                    </p>
                  )}
                </div>

                {isEditing && (
                  <div className="space-y-2 pt-4 border-t">
                    <label className="text-sm font-medium flex items-center space-x-2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span>Nouveau mot de passe (optionnel)</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        data-testid="edit-password-input"
                        className="w-full h-12 px-4 pr-12 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        placeholder="Laisser vide pour ne pas changer"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Subscription Section */}
          <div className="space-y-6">
            <div className="bg-card p-6 rounded-xl border shadow-sm">
              <div className="flex items-center space-x-3 mb-6">
                <CreditCard className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-semibold">Abonnement</h2>
              </div>

              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${isPackActive() ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`h-3 w-3 rounded-full ${isPackActive() ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className={`font-semibold ${isPackActive() ? 'text-green-600' : 'text-red-600'}`}>
                      {isPackActive() ? 'Actif' : 'Expiré'}
                    </span>
                  </div>
                  <p className="text-2xl font-bold">{user?.type_pack || 'Gratuit'}</p>
                </div>

                {user?.debut_abonnement && (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Début :</span>
                    </div>
                    <p className="font-medium">
                      {new Date(user.debut_abonnement).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}

                {user?.fin_abonnement && (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Fin :</span>
                    </div>
                    <p className={`font-medium ${!isPackActive() ? 'text-red-600' : ''}`}>
                      {new Date(user.fin_abonnement).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    {isPackActive() && (
                      <p className="text-sm text-muted-foreground">
                        ({daysRemaining()} jours restants)
                      </p>
                    )}
                  </div>
                )}

                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Essais IA</span>
                    <span className="font-bold text-primary">{user?.credits_ia || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {!isPackActive() && (
              <a
                href="/tarification"
                data-testid="upgrade-pack-link"
                className="block w-full py-4 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold text-center"
              >
                Renouveler mon abonnement
              </a>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
